import { ApiOutlined, BugOutlined, DeleteOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@apollo/client/react';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Card, Descriptions, Empty, Popconfirm, Popover, Space } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { MainContent } from '../../../components/MainContent';
import type { EcardConfigsQuery } from '../../../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../../../hooks/store/useCurrentContextStore.ts';
import { hasSomeRole, Role } from '../../../utils/user.ts';
import { DELETE_ECARD_CONFIG_MUTATION, ECARD_CONFIGS_QUERY } from './EcardConfigs.gql';
import { EcardDebuggerModal } from './EcardDebuggerModal.tsx';
import { EcardTest } from './EcardTest.tsx';

export type EcardConfig = NonNullable<EcardConfigsQuery['ecardConfigs']>[number];

export const EcardConfigs: React.FC<{ onShowAssistant: () => void }> = ({ onShowAssistant }) => {
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const auth = useAuth();
  const isUserRole = hasSomeRole([Role.ROLE_LR_USER], auth.user);
  const isMedcomRole = hasSomeRole([Role.ROLE_LR_MEDCOM], auth.user);
  const { message } = App.useApp();
  const [deleteEcardConfigMutation, { loading: deleteEcardConfigLoading }] = useMutation(DELETE_ECARD_CONFIG_MUTATION);
  const [debuggerConfig, setDebuggerConfig] = useState<EcardConfig | null>(null);

  const { data, loading, refetch } = useQuery(ECARD_CONFIGS_QUERY, {
    variables: {
      doctorId: isUserRole ? currentDoctorId : primaryDoctorId,
    },
  });

  const deleteEcardConfig = async (id: string) => {
    try {
      await deleteEcardConfigMutation({
        variables: {
          id: id,
        },
      });

      refetch();
    } catch {
      message.error('e-card Konfiguration konnte nicht entfernt werden');
    }
  };

  return (
    <MainContent size="medium">
      <PageHeader
        title={`e-card Einstellungen für ${data?.doctor?.name}`}
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={
          <Button type="primary" onClick={onShowAssistant}>
            Einrichtungsassistent starten
          </Button>
        }
      />
      <Space size="large" orientation="vertical" style={{ width: '100%' }}>
        {loading && <LoadingIndicator />}
        {!loading && data?.ecardConfigs && data.ecardConfigs.length === 0 && (
          <Empty description="Es wurde noch keine e-card konfiguriert" image={Empty.PRESENTED_IMAGE_DEFAULT} />
        )}
        {!loading &&
          data?.ecardConfigs?.map((config, i) => (
            <Card
              type="inner"
              size="small"
              key={`ecard-config-${i}`}
              title={config.cardReaderName}
              extra={
                <Space>
                  {isMedcomRole && (
                    <Button type="text" onClick={() => setDebuggerConfig(config)} icon={<BugOutlined />}>
                      Debugger öffnen
                    </Button>
                  )}
                  <Popover content={<EcardTest config={config} />} destroyOnHidden placement="bottomRight" arrow trigger={['click']}>
                    <Button type="text" icon={<ApiOutlined />}>
                      Funktionstest
                    </Button>
                  </Popover>
                  <Popconfirm
                    title="Wollen Sie die e-card Konfiguration wirklich entfernen?"
                    onConfirm={() => deleteEcardConfig(config.id)}
                    okText="Ja"
                    okButtonProps={{ danger: true }}
                    cancelText="Nein"
                  >
                    <Button danger type="text" icon={<DeleteOutlined />} loading={deleteEcardConfigLoading}>
                      Entfernen
                    </Button>
                  </Popconfirm>
                </Space>
              }
            >
              <Descriptions size="small" column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2, xxxl: 2 }} styles={{ label: { width: 160 } }}>
                <Descriptions.Item label="Anschluss VPNR">
                  <span className={monoFont}>{config.vpnr}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Kartenleser-Adresse">
                  <span className={monoFont}>{config.cardReaderAddress}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Ordination">{config.ordinationName}</Descriptions.Item>
                <Descriptions.Item label="Tätigkeitsbereich">{config.taetigkeitsBereichName}</Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
      </Space>
      <EcardDebuggerModal config={debuggerConfig} onClose={() => setDebuggerConfig(null)} />
    </MainContent>
  );
};
